import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import type { AvailabilityImportUploadResponse } from '@ooh/contracts';
import { HeaderRowSelector } from './header-row-selector';
import { usePreviewWithHeaderRowOverride } from '../api/mutations';

interface HeaderRowPhaseProps {
  jobId: number;
  uploadResponse: AvailabilityImportUploadResponse;
  fileName: string;
  fileSizeKb: number;
  onBack: () => void;
  /** Called once the (possibly overridden) preview has been fetched and
   * cached — `undefined` when the operator kept the auto-detected row. */
  onNext: (headerRowIndex: number | undefined) => void;
}

/**
 * New wizard step between Upload and Preview (CU-86cb1npz2, gap #1): lets
 * the operator confirm or override `detectHeaderRow`'s automatic pick
 * before the file is re-parsed against it. "Dalej" fetches
 * `GET /:id/preview` with the chosen row (omitting the param entirely when
 * it equals the auto-detected row, so that request stays byte-identical to
 * the pre-gap-1 behavior) and primes the query cache so the following
 * `PreviewPhase` doesn't re-fetch. "Wstecz" returns to Upload.
 */
export function HeaderRowPhase({
  jobId,
  uploadResponse,
  fileName,
  fileSizeKb,
  onBack,
  onNext,
}: HeaderRowPhaseProps) {
  const { t } = useTranslation('availabilityImport');
  const [selectedRowIndex, setSelectedRowIndex] = React.useState(
    uploadResponse.detectedHeaderRowIndex,
  );
  const mutation = usePreviewWithHeaderRowOverride(jobId, onNext);

  function handleNext() {
    const override =
      selectedRowIndex !== uploadResponse.detectedHeaderRowIndex ? selectedRowIndex : undefined;
    mutation.mutate(override);
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="phase-header-row">
      <HeaderRowSelector
        sourceBarProps={{
          fileName,
          fileSizeKb,
          estimatedRows: uploadResponse.totalRows,
          sheets: [],
          selectedSheet: '',
          onSheetChange: () => {},
          labels: {
            fileSummaryTemplate: t('headerRow.fileSummaryTemplate'),
            fileSummaryTemplateRowsOnly: t('headerRow.fileSummaryTemplateRowsOnly'),
            sheetPickerLabel: t('headerRow.sheetPickerLabel'),
          },
        }}
        rows={uploadResponse.rawRows}
        detectedRowIndex={uploadResponse.detectedHeaderRowIndex}
        selectedRowIndex={selectedRowIndex}
        onSelectRow={setSelectedRowIndex}
        labels={{
          heading: t('headerRow.heading'),
          helperText: t('headerRow.helperText'),
          rowNumberHeader: t('headerRow.rowNumberHeader'),
          previewHeading: t('headerRow.previewHeading'),
          previewHeaderRowLabel: t('headerRow.previewHeaderRowLabel'),
          previewSampleRowLabel: t('headerRow.previewSampleRowLabel'),
          previewNoSampleRow: t('headerRow.previewNoSampleRow'),
          emptyCellPlaceholder: t('headerRow.emptyCellPlaceholder'),
          emptyRowPlaceholder: t('headerRow.emptyRowPlaceholder'),
          headerAutoNoteDetectedTemplate: t('headerRow.headerAutoNoteDetectedTemplate'),
          headerAutoNoteManualTemplate: t('headerRow.headerAutoNoteManualTemplate'),
          headerAutoInfoLabel: t('headerRow.headerAutoInfoLabel'),
          headerAutoInfoText: t('headerRow.headerAutoInfoText'),
        }}
      />
      <div className="flex justify-between pt-2">
        <Button type="button" variant="outline" onClick={onBack} data-testid="back-to-upload">
          {t('headerRow.back')}
        </Button>
        <Button
          type="button"
          onClick={handleNext}
          disabled={mutation.isPending}
          data-testid="next-to-preview"
        >
          {mutation.isPending ? t('headerRow.loading') : t('headerRow.nextButton')}
        </Button>
      </div>
    </div>
  );
}
